import { toast } from 'sonner';

import type { ContactHandle } from '@/components/carrier-directory/types';
import {
  useAddHandleMutation,
  useArchiveCarrierMutation,
  useListCarriersQuery,
  useUpdateHandleMutation,
} from '@/services/api/carriersApiService';

export const useCarriers = () => {
  const { data: carriers = [], isLoading } = useListCarriersQuery();
  const [addHandleMutation] = useAddHandleMutation();
  const [updateHandleMutation] = useUpdateHandleMutation();
  const [archiveCarrierMutation] = useArchiveCarrierMutation();

  const addHandle = async (
    carrierId: string,
    handleType: string,
    value?: string | null,
    name?: string | null,
    description?: string | null,
    divisionId?: string | null,
    addressLine1?: string | null,
    addressLine2?: string | null,
    city?: string | null,
    state?: string | null,
    zip?: string | null,
  ) => {
    try {
      const handle = await addHandleMutation({
        carrierId,
        handleType,
        value,
        name,
        description,
        divisionId,
        addressLine1,
        addressLine2,
        city,
        state,
        zip,
      }).unwrap();

      toast.success('Contact added');
      return handle;
    } catch (error) {
      toast.error('Failed to add contact');
      throw error;
    }
  };

  const updateHandle = async (
    handleId: string,
    updates: Partial<ContactHandle>,
  ) => {
    try {
      const updated = await updateHandleMutation({
        handleId,
        updates,
      }).unwrap();

      toast.success('Contact updated');
      return updated;
    } catch (error) {
      toast.error('Failed to update contact');
      throw error;
    }
  };

  const deleteCarrier = async (carrierId: string) => {
    try {
      await archiveCarrierMutation({ carrierId }).unwrap();
      toast.success('Carrier deleted');
    } catch (error) {
      toast.error('Failed to delete carrier');
      throw error;
    }
  };

  return {
    carriers,
    loading: isLoading,
    addHandle,
    updateHandle,
    deleteCarrier,
  };
};
